import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useWs } from "@/context/WorkspaceContext";
import { EmptyState, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, FileText, Trash2, ArrowLeft, Calendar, Loader2, Pencil } from "lucide-react";

export default function ChannelReports() {
  const { canEdit, slug } = useWs();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", date_start: "", date_end: "", html_content: "" });
  const [editing, setEditing] = useState(null); // report being edited
  const [editForm, setEditForm] = useState({ name: "", date_start: "", date_end: "", html_content: "" });
  const [editSaving, setEditSaving] = useState(false);
  const isAdmin = canEdit;

  const load = async () => {
    setLoading(true);
    try {
      const [r, ch] = await Promise.all([
        api.get(`/channels/${channelId}/reports`),
        api.get("/channels"),
      ]);
      setReports(r.data);
      setChannel(ch.data.find((c) => c.id === channelId));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channelId]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/reports", { ...form, channel_id: channelId });
      toast.success("Report created");
      setOpen(false);
      setForm({ name: "", date_start: "", date_end: "", html_content: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!(await confirmDelete({ message: "Delete this report?" }))) return;
    await api.delete(`/reports/${id}`);
    toast.success("Deleted");
    load();
  };

  const openEdit = (r, e) => {
    e.stopPropagation();
    setEditing(r);
    setEditForm({
      name: r.name || "",
      date_start: r.date_start ? r.date_start.slice(0, 10) : "",
      date_end: r.date_end ? r.date_end.slice(0, 10) : "",
      html_content: r.html_content || "",
    });
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/reports/${editing.id}`, { ...editForm, channel_id: channelId });
      toast.success("Report updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div>
      <button data-testid="reports-back-button" onClick={() => navigate(`/${slug}/reports`)} className="flex items-center gap-1.5 text-sm text-[#9FA1A4] hover:text-[#06060C] mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> All channels
      </button>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#723FFF] mb-2">Channel</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{channel?.name || "Reports"}</h1>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-report-button" className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New report</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-2xl">
              <DialogHeader><DialogTitle className="font-display">New report</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <Field label="Report name">
                  <Input data-testid="report-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="March Performance" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Period start">
                    <Input data-testid="report-start-input" type="date" value={form.date_start} onChange={(e) => setForm({ ...form, date_start: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" />
                  </Field>
                  <Field label="Period end">
                    <Input data-testid="report-end-input" type="date" value={form.date_end} onChange={(e) => setForm({ ...form, date_end: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" />
                  </Field>
                </div>
                <Field label="HTML content">
                  <Textarea data-testid="report-html-input" value={form.html_content} onChange={(e) => setForm({ ...form, html_content: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF] min-h-[160px] font-mono-data text-xs" placeholder="<div>Paste your externally-built HTML report here…</div>" />
                </Field>
              </div>
              <DialogFooter>
                <Button data-testid="report-submit-button" onClick={create} disabled={saving || !form.name || !form.html_content} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create report"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-[#9FA1A4] text-sm">Loading…</div>
      ) : reports.length === 0 ? (
        <EmptyState icon={FileText} title="No reports yet" description={isAdmin ? "Create a report by pasting externally-built HTML." : "Reports will appear here once published."} testid="channel-reports-empty" />
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div
              key={r.id}
              data-testid="report-list-item"
              onClick={() => navigate(`/${slug}/reports/${channelId}/${r.id}`)}
              className="group cursor-pointer flex items-center gap-4 rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-5 hover:border-[#d3d4da] transition-all"
            >
              <div className="h-10 w-10 rounded-lg bg-[#F6F6FD] border border-[#f0f0f3] flex items-center justify-center text-[#9FA1A4]">
                <FileText className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-[#06060C]">{r.name}</h3>
                {(r.date_start || r.date_end) && (
                  <p className="mt-0.5 flex items-center gap-1.5 font-mono-data text-[10px] text-[#9FA1A4]">
                    <Calendar className="h-3 w-3" /> {fmtDate(r.date_start)} – {fmtDate(r.date_end)}
                  </p>
                )}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                  <button data-testid="report-edit-button" onClick={(e) => openEdit(r, e)} className="text-[#c4c5cc] hover:text-[#06060C] transition-colors p-1">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid="report-delete-button" onClick={(e) => remove(r.id, e)} className="text-[#c4c5cc] hover:text-rose-600 transition-colors p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit report dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Edit report</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Report name">
              <Input data-testid="edit-report-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="March Performance" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Period start">
                <Input data-testid="edit-report-start-input" type="date" value={editForm.date_start} onChange={(e) => setEditForm({ ...editForm, date_start: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" />
              </Field>
              <Field label="Period end">
                <Input data-testid="edit-report-end-input" type="date" value={editForm.date_end} onChange={(e) => setEditForm({ ...editForm, date_end: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" />
              </Field>
            </div>
            <Field label="HTML content">
              <Textarea data-testid="edit-report-html-input" value={editForm.html_content} onChange={(e) => setEditForm({ ...editForm, html_content: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF] min-h-[160px] font-mono-data text-xs" />
            </Field>
          </div>
          <DialogFooter>
            <Button data-testid="edit-report-submit-button" onClick={saveEdit} disabled={editSaving || !editForm.name || !editForm.html_content} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
